import styled from "styled-components";
import { colors } from "styles/color-tokens";
import {
  UpgradePlanTextRequestText,
  UpsellLegacySection,
  UpsellPlansContext,
  UpsellUpgradePlan,
} from "./upsell-plans";
import { SkedPlanCodeName, UpsellFeatureType } from "./types";
import IncreaseAccountQuota from "./increase-account-quota";
import { UpsellFooter } from "./upsell-footer";
import {
  enterpriseFeatures,
  essentialsFeatures,
  fundamentalsFeatures,
  professionalFeatures,
} from "./constants/plans";
import { UpsellHeader } from "./common-components";
import {
  findPlanByPlanCodeName,
  getAddonIdForFeature,
  isUserPlan,
} from "./plan-utils";
import { User } from "features/user";
import { Button } from "@/components/ui/button";
import { goToManagePlan } from "utils/manage-plan";

const Header = UpsellHeader;

export const UpsellContent = ({
  plansContext,
  user,
}: {
  plansContext: UpsellPlansContext;
  user: User;
}) => {
  switch (plansContext.triggerFeature) {
    case "approvals": {
      return <ApprovalsUpsellContent plansContext={plansContext} user={user} />;
    }
    case "internal-approvals": {
      return (
        <InternalApprovalsAddonUpsellContent
          plansContext={plansContext}
          user={user}
        />
      );
    }
    case "external-approvals": {
      return (
        <ExternalApprovalsAddonUpsellContent
          plansContext={plansContext}
          user={user}
        />
      );
    }
    case "labels-planning": {
      return (
        <LabelsPlanningUpsellContent plansContext={plansContext} user={user} />
      );
    }
    case "standard-pdf": {
      return (
        <StandardPDFUpsellContent plansContext={plansContext} user={user} />
      );
    }
    case "competitor-analytics": {
      return (
        <CompetitorAnalyticsUpsellContent
          plansContext={plansContext}
          user={user}
        />
      );
    }
    case "paid-ads": {
      return <PaidAdsUpsellContent plansContext={plansContext} user={user} />;
    }
    case "excel": {
      return <ExcelUpsellContent plansContext={plansContext} user={user} />;
    }
    case "comparison-period": {
      return plansContext.currentPlan === "sked-professional" ? (
        <ComparisonPeriodFromProfessionallUpsellContent
          plansContext={plansContext}
          user={user}
        />
      ) : (
        <ComparisonPeriodFromEssentialUpsellContent
          plansContext={plansContext}
          user={user}
        />
      );
    }
    case "labels-reporting": {
      return plansContext.currentPlan === "sked-professional" ? (
        <LabelsReportingFromProfessionalUpsellContent
          plansContext={plansContext}
          user={user}
        />
      ) : (
        <LabelsReportingFromEssentialUpsellContent
          plansContext={plansContext}
          user={user}
        />
      );
    }
    case "increase-account-quota": {
      return <IncreaseAccountQuota />;
    }
    case "teamwork": {
      return <TeamworkUpsellContent plansContext={plansContext} user={user} />;
    }
    case "user-generated-content": {
      return <UserGeneratedContent plansContext={plansContext} user={user} />;
    }
    case "add-account-user": {
      return <AddAccountUserContent plansContext={plansContext} user={user} />;
    }
    case "brand-summary": {
      return (
        <BrandSummaryUpsellContent plansContext={plansContext} user={user} />
      );
    }
    case "frontify-media": {
      return <AddFrontifyMedia plansContext={plansContext} user={user} />;
    }
    case "report-builder-professional":
    case "report-builder-enterprise": {
      return <ReportBuilderContent plansContext={plansContext} user={user} />;
    }
    case "ai-analytics": {
      return (
        <AIAnalyticsUpsellContent plansContext={plansContext} user={user} />
      );
    }
    default: {
      return null;
    }
  }
};

interface UpsellContentProps {
  plansContext: UpsellPlansContext;
  downgrade?: boolean;
  user: User;
}

const ApprovalsUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Unlock the Power of Workflows and Streamlined{" "}
        <HeaderFeatureText>Approvals</HeaderFeatureText>
      </Header>
      <Subtitle>
        Streamline content approval with custom workflows tailored to your
        process. Share a live calendar of scheduled content with reviewers and
        stakeholders.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={0}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-approvals.png"
            alt=""
            width="350"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

/**
 * Add-on upsell for the Internal Approvals product. Reuses the approvals
 * art and a slimmed-down PlanUpgradeSection. The primary CTA navigates to
 * `/onboarding/manage-plan?addon=<id>` via `goToManagePlan`; the addon ID
 * comes from `getAddonIdForFeature(feature)` (plan-utils). UpsellFooter
 * still provides the secondary Contact Support / View all plans links.
 */
const InternalApprovalsAddonUpsellContent = (props: UpsellContentProps) => {
  const addonId = getAddonIdForFeature("internal-approvals");
  return (
    <MainContainer>
      <Header>
        Customize your{" "}
        <HeaderFeatureText>post statuses and workflows</HeaderFeatureText>
      </Header>
      <Subtitle>
        Your plan includes 2 default post statuses. Add the Internal Approvals
        add-on to create unlimited custom statuses with your own colors and
        publishing rules — perfect for matching your team&apos;s review process.
      </Subtitle>
      <PlanUpgradeSection fromPlan={false}>
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-approvals.png"
            alt=""
            width="350"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      {addonId && (
        <div className="flex justify-center mt-4 mb-2">
          <Button
            variant="blue-primary"
            size="md"
            onClick={() => goToManagePlan({ addon: addonId })}
          >
            Add Internal Approvals
          </Button>
        </div>
      )}
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

/**
 * Add-on upsell for the External Approvals product. Same shape as the
 * internal variant — both deeplink to the manage-plan page with the relevant
 * `?addon=` query string for the right card to be highlighted.
 */
const ExternalApprovalsAddonUpsellContent = (props: UpsellContentProps) => {
  const addonId = getAddonIdForFeature("external-approvals");
  return (
    <MainContainer>
      <Header>
        Loop in <HeaderFeatureText>external reviewers</HeaderFeatureText>
      </Header>
      <Subtitle>
        Add the External Approvals add-on to share a calendar view of upcoming
        posts with clients and stakeholders, collect approvals via a public
        link, and let reviewers leave comments — no Sked login required.
      </Subtitle>
      <PlanUpgradeSection fromPlan={false}>
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-approvals.png"
            alt=""
            width="350"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      {addonId && (
        <div className="flex justify-center mt-4 mb-2">
          <Button
            variant="blue-primary"
            size="md"
            onClick={() => goToManagePlan({ addon: addonId })}
          >
            Add External Approvals
          </Button>
        </div>
      )}
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const LabelsPlanningUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Upgrade to unlock{" "}
        <HeaderFeatureText>planning with Labels</HeaderFeatureText>
      </Header>
      <Subtitle>
        Fast-track planning with AI-recommended content pillars based on past
        posts. Use AI to brainstorm more content pillar and campaign ideas, and
        keep content organized and easily trackable with Labels.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={2}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/no-access.png"
            alt=""
            width="572"
            height="378"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const StandardPDFUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Save time with presentation-ready{" "}
        <HeaderFeatureText>reports</HeaderFeatureText>
      </Header>
      <Subtitle>
        Generate professional PDF reports and instantly share top-line metrics
        with your team, clients and stakeholders.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={5}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-pdf.png"
            alt=""
            width="408"
            height="272"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const CompetitorAnalyticsUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Upgrade to unlock{" "}
        <HeaderFeatureText>Competitor Analytics</HeaderFeatureText>
      </Header>
      <Subtitle>
        Track your performance vs competitors and industry trendsetters. Get an
        AI summary of their strategies plus ideas to counter or match.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={3}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-competitor-analytics.png"
            alt=""
            width="351"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const PaidAdsUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Unlock the Power of{" "}
        <HeaderFeatureText>Insights for Paid Ads</HeaderFeatureText> Accounts
      </Header>
      <Subtitle>
        Supercharge your advertising campaigns on Meta with Paid Ad Insights,
        empowering you with valuable analytics and actionable recommendations to
        optimise performance.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={5}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-paid-ads.png"
            alt=""
            width="408"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const ExcelUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Enhance Your Insights with{" "}
        <HeaderFeatureText>Excel Reports</HeaderFeatureText>
      </Header>
      <Subtitle>
        Increase the flexibility of your data with Excel reports. Select a
        custom date range and a report will be emailed directly to you.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={5}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-excel.png"
            alt=""
            width="408"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const ComparisonPeriodFromEssentialUpsellContent = (
  props: UpsellContentProps
) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        <HeaderFeatureText>Compare metrics</HeaderFeatureText> between two time
        periods of your selection
      </Header>
      <Subtitle>
        Get deeper insights into trends and seasonality. Compare year-on-year
        performance, month-on-month, or any two time periods that fit your
        reporting needs.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={2}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...resolveFromPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-comparison-period.png"
            alt=""
            width="408"
            height="239"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const LabelsReportingFromEssentialUpsellContent = (
  props: UpsellContentProps
) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Unlock{" "}
        <HeaderFeatureText>
          Content Pillar and Campaign Insights
        </HeaderFeatureText>{" "}
        with Labels
      </Header>
      <Subtitle>
        Organize and categorize content by content pillars and campaigns. Refine
        your content strategy with insights on content pillar and campaign
        performance across channels and accounts.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={0}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...resolveFromPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-labels-reporting.png"
            alt=""
            width="408"
            height="272"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const ComparisonPeriodFromProfessionallUpsellContent = (
  props: UpsellContentProps
) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    "sked-professional"
  );

  return (
    <MainContainer>
      <Header>
        <HeaderFeatureText>Compare metrics</HeaderFeatureText> between two time
        periods of your selection
      </Header>
      <Subtitle>
        Get deeper insights into trends and seasonality. Compare year-on-year
        performance, month-on-month, or any two time periods that fit your
        reporting needs.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          fromPlan={fromPlan}
          fromPlanFeatures={professionalFeatures}
          fromPlanLabel={props.user.planLabel ?? "Grow"}
          highlightItemIndex={2}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-comparison-period.png"
            alt=""
            width="408"
            height="239"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const LabelsReportingFromProfessionalUpsellContent = (
  props: UpsellContentProps
) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    "sked-professional"
  );

  return (
    <MainContainer>
      <Header>
        Unlock{" "}
        <HeaderFeatureText>
          Content Pillar and Campaign Insights
        </HeaderFeatureText>{" "}
        with Labels
      </Header>
      <Subtitle>
        Organize and categorize content by content pillars and campaigns. Refine
        your content strategy with insights on content pillar and campaign
        performance across channels and accounts.
      </Subtitle>
      <UpgradePlanTextRequest
        show={!props.plansContext.isLegacyPlan}
        text="Enhance efficiency and unlock advanced features by upgrading your plan."
      />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          fromPlan={fromPlan}
          fromPlanFeatures={professionalFeatures}
          fromPlanLabel={props.user.planLabel ?? "Grow"}
          highlightItemIndex={0}
          {...resolveToPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-labels-reporting.png"
            alt=""
            width="408"
            height="272"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter
        feature={props.plansContext.triggerFeature}
        infoText="Any questions?"
      />
    </MainContainer>
  );
};

const TeamworkUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Unlock the Power of <HeaderFeatureText>Teamwork</HeaderFeatureText>
      </Header>
      <Subtitle>
        Collaborate with as many users as you need throughout your process.
        Brainstorm ideas as team, work together on posts, assign conversations
        in inbox, and leave each other notes and comments so everyone is on the
        same page from start to finish.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={1}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...fundamentalsPlanProps}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-teamwork.png"
            alt=""
            width="408"
            height="328"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const UserGeneratedContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        Unlock the Power of{" "}
        <HeaderFeatureText>User Generated Content</HeaderFeatureText>
      </Header>
      <Subtitle>
        Unlock the full potential of your brand by leveraging our User Generated
        Content features.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={undefined}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...fundamentalsPlanProps}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-ugc.png"
            alt=""
            width="408"
            height="272"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const AddAccountUserContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <Header>
        <HeaderFeatureText>Upgrade your plan</HeaderFeatureText>
      </Header>
      <Subtitle>
        You{"'"}re currently on the Basic plan which includes 1 social set and
        one user only. Upgrade to increase your number of included accounts,
        plus unlock more features like additional users and collaboration.
      </Subtitle>
      <UpgradePlanTextRequest show={!props.plansContext.isLegacyPlan} />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={0}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...fundamentalsPlanProps}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-add-account-user.png"
            alt=""
            width="350"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const AddFrontifyMedia = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );
  return (
    <MainContainer>
      <Header>
        Integrate
        <HeaderFeatureText> Frontify </HeaderFeatureText>
        for seamless content creation
      </Header>
      <Subtitle>
        Link Sked with your Frontify asset library to streamline your workflow.
      </Subtitle>
      <UpgradePlanTextRequest
        show={!props.plansContext.isLegacyPlan}
        text="Enhance efficiency and unlock advanced features by upgrading your plan."
      />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={-1}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...resolveFromPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-add-frontify-media.png"
            alt=""
            width="408"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

export const resolveReportBuilderFeatureName = (user: User) => {
  const upsellReportBuilderPro = isUserPlan(user, "sked-professional");

  return upsellReportBuilderPro
    ? "report-builder-enterprise"
    : "report-builder-professional";
};

const ReportBuilderContent = (props: UpsellContentProps) => {
  const toPlanName = resolveToPlanProps(props).toPlanName;
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      {toPlanName === "sked-enterprise" ? (
        <>
          <Header>
            Save time with our
            <HeaderFeatureText> custom reports</HeaderFeatureText>
          </Header>
          <Subtitle>
            From a one-off analysis to recurring updates, personalize, generate
            and automatically send reports to your team, managers, clients or
            other stakeholders.
          </Subtitle>
        </>
      ) : (
        <>
          <Header>
            Save time with
            <HeaderFeatureText> presentation-ready reports</HeaderFeatureText>
          </Header>
          <Subtitle>
            Generate professional PDF reports and instantly share top-line
            metrics with your team, clients and stakeholders.
          </Subtitle>
        </>
      )}
      <UpgradePlanTextRequest
        show={!props.plansContext.isLegacyPlan}
        text="To get access to this feature, please upgrade your plan:"
      />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={0}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...resolveFromPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-report-builder.png"
            alt=""
            width="280"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter
        feature={props.plansContext.triggerFeature}
        infoText="Any questions?"
      />
    </MainContainer>
  );
};

const BrandSummaryUpsellContent = (props: UpsellContentProps) => {
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );
  const isProfessionalPlan =
    props.plansContext.currentPlan === "sked-professional";
  return (
    <MainContainer>
      <Header>
        {isProfessionalPlan ? (
          <>
            Compare performance across
            <HeaderFeatureText> custom time periods</HeaderFeatureText>
          </>
        ) : (
          "Customize your reporting period for sharper insights"
        )}
      </Header>

      <Subtitle>
        {isProfessionalPlan
          ? "Get deeper insights into trends and seasonality. Compare year-on-year performance, month-on-month, or any two time periods that fit your reporting needs."
          : "Dive deeper into your brand's performance metrics by customizing your reporting period beyond the standard 30 days."}
      </Subtitle>
      <UpgradePlanTextRequest
        show={!props.plansContext.isLegacyPlan}
        text="Boost efficiency and unlock advanced features by upgrading your plan."
      />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={isProfessionalPlan ? 2 : 6}
          fromPlan={fromPlan}
          {...resolveFromPlanProps(props)}
          {...resolveToPlanProps(
            isProfessionalPlan
              ? {
                  ...props,
                  plansContext: {
                    ...props.plansContext,
                    triggerFeature: "comparison-period",
                  },
                }
              : props
          )}
        />
        <FeatureImageContainer>
          <img
            src={
              isProfessionalPlan
                ? "/assets/img/upsell/upsell-comparison-period.png"
                : "/assets/img/brand-summary-upsell.png"
            }
            alt=""
            width={isProfessionalPlan ? "408" : "350"}
            height={isProfessionalPlan ? "239" : "280"}
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature={props.plansContext.triggerFeature} />
    </MainContainer>
  );
};

const AIAnalyticsUpsellContent = (props: UpsellContentProps) => {
  // const toPlanName = resolveToPlanProps(props).toPlanName;
  const fromPlan = findPlanByPlanCodeName(
    props.plansContext.plans,
    props.plansContext.periodUnit,
    resolveFromPlanProps(props).fromPlanName
  );

  return (
    <MainContainer>
      <>
        <Header>
          Upgrade to unlock the power of
          <HeaderFeatureText> AI Analytics</HeaderFeatureText>
        </Header>
        <Subtitle>
          See performance at brand level along with an AI analysis of metrics
          across social media accounts
        </Subtitle>
      </>
      <UpgradePlanTextRequest
        show={!props.plansContext.isLegacyPlan}
        text="Enhance efficiency and unlock advanced features by upgrading your plan."
      />
      <PlanUpgradeSection fromPlan={!!fromPlan}>
        <UpsellUpgradePlan
          plansContext={props.plansContext}
          highlightItemIndex={3}
          fromPlan={fromPlan}
          {...resolveToPlanProps(props)}
          {...resolveFromPlanProps(props)}
        />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-ai-analytics.png"
            alt=""
            style={{
              objectFit: "contain",
              width: "100%",
              height: "100%",
            }}
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter
        feature={props.plansContext.triggerFeature}
        infoText="Any questions?"
      />
    </MainContainer>
  );
};

export const GenericLegacyPlanContent = () => {
  return (
    <MainContainer>
      <Header>
        <HeaderFeatureText>Choose Plan</HeaderFeatureText>
      </Header>
      <Subtitle> </Subtitle>
      <UpgradePlanTextRequest show={false} />
      <PlanUpgradeSection>
        <UpsellLegacySection feature="unknown" />
        <FeatureImageContainer>
          <img
            src="/assets/img/upsell/upsell-add-account-user.png"
            alt=""
            width="350"
            height="280"
          />
        </FeatureImageContainer>
      </PlanUpgradeSection>
      <UpsellFooter feature="unknown" />
    </MainContainer>
  );
};

const UpgradePlanTextRequest = ({
  show,
  text,
}: {
  show: boolean;
  text?: string;
}) => {
  if (!show) {
    return null;
  }
  if (text) {
    return <UpgradePlanTextRequestText>{text}</UpgradePlanTextRequestText>;
  }

  return (
    <UpgradePlanTextRequestText>
      To get access to this feature, please upgrade your plan:
    </UpgradePlanTextRequestText>
  );
};

const resolveFromPlanProps = (props: UpsellContentProps) => {
  if (props.plansContext.currentPlan === "sked-essentials") {
    return {
      ...essentialsPlanProps,
      fromPlanLabel: props.user.planLabel,
    };
  }

  if (props.plansContext.currentPlan === "sked-professional") {
    return {
      ...professionalPlanProps,
      fromPlanLabel: props.user.planLabel,
    };
  }

  return fundamentalsPlanProps;
};
const resolveToPlanProps = (props: UpsellContentProps) => {
  const { plansContext } = props;
  const toPlan = resolvePlanByFeature(plansContext.triggerFeature);
  switch (toPlan) {
    case "sked-enterprise": {
      return {
        toPlanName: toPlan,
        toPlanFeatures: enterpriseFeatures,
        toPlanLabel: "Accelerate",
      };
    }
    case "sked-professional": {
      return {
        toPlanName: toPlan,
        toPlanFeatures: professionalFeatures,
        toPlanLabel: "Grow",
      };
    }
    case "sked-essentials": {
      return {
        toPlanName: toPlan,
        toPlanFeatures: essentialsFeatures,
        toPlanLabel: "Launch",
      };
    }
    default: {
      return {
        toPlanName: toPlan,
        toPlanFeatures: fundamentalsFeatures,
        toPlanLabel: "Launch",
      };
    }
  }
};

const fundamentalsPlanProps = {
  fromPlanName: "sked-fundamentals" as SkedPlanCodeName,
  fromPlanFeatures: fundamentalsFeatures,
  fromPlanLabel: "Basic",
};

const essentialsPlanProps = {
  fromPlanName: "sked-essentials" as SkedPlanCodeName,
  fromPlanFeatures: essentialsFeatures,
  fromPlanLabel: "Launch",
};

const professionalPlanProps = {
  fromPlanName: "sked-professional" as SkedPlanCodeName,
  fromPlanFeatures: professionalFeatures,
  fromPlanLabel: "Grow",
};

export function resolvePlanByFeature(
  triggerFeature: UpsellFeatureType
): SkedPlanCodeName {
  switch (triggerFeature) {
    case "comparison-period":
    case "labels-reporting":
    case "paid-ads":
    case "frontify-media":
    case "report-builder-enterprise":
    case "ai-analytics": {
      return "sked-enterprise";
    }
    case "add-account-user":
    case "increase-account-quota":
    case "user-generated-content":
    case "teamwork": {
      return "sked-essentials";
    }
    default: {
      return "sked-professional";
    }
  }
}

const FeatureImageContainer = styled.div`
  margin-left: 40px;
  display: flex;
  align-items: center;
  width: 408px;
`;

const PlanUpgradeSection = styled.div<{ fromPlan?: boolean }>`
  display: flex;
  overflow: hidden;

  ${({ fromPlan }) =>
    !fromPlan &&
    `
      justify-content: center;
    `}
`;

const MainContainer = styled.div``;

const HeaderFeatureText = styled.span`
  color: ${colors.primary500};
`;

const Subtitle = styled.span`
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${colors.neutral800};
`;
